import { io, type Socket } from 'socket.io-client';
import { tokenStore, refreshAccessToken } from '../api/client';

// Single shared Socket.IO connection for RT Messages. Connects to the same origin
// (nginx proxies /socket.io → api) with the JWT in the handshake.
//
// Reliability contract (P1):
//  - Auto-reconnects on transport drops (infinite attempts, backoff).
//  - On an AUTH connect error (expired/revoked access token), refreshes the token once and
//    reconnects — the user never has to log out/in to make chat work again.
//  - Exposes reconnect + connection-status signals so the UI can re-sync missed state and
//    show a "reconnecting" indicator.

let socket: Socket | null = null;
let hasConnectedOnce = false; // distinguishes the first connect from a later reconnect
let refreshing = false;

type Handler = () => void;
type StatusHandler = (connected: boolean) => void;
const reconnectHandlers = new Set<Handler>();
const statusHandlers = new Set<StatusHandler>();

function notifyStatus(connected: boolean) {
  for (const h of statusHandlers) h(connected);
}

export function getSocket(): Socket {
  if (socket) return socket;
  socket = io({
    path: '/socket.io',
    // Function form is re-evaluated on every (re)connect, so reconnects always use
    // the latest access token (survives token refresh).
    auth: (cb) => cb({ token: tokenStore.access ?? '' }),
    transports: ['websocket', 'polling'],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    timeout: 10000,
  });

  socket.on('connect', () => {
    const wasReconnect = hasConnectedOnce;
    hasConnectedOnce = true;
    notifyStatus(true);
    // On a genuine reconnect the server rebuilds room membership from the DB; the client must
    // re-sync anything it missed while offline (fire listeners so pages refetch).
    if (wasReconnect) for (const h of reconnectHandlers) h();
  });

  socket.on('disconnect', () => notifyStatus(false));

  socket.on('connect_error', (err: Error) => {
    notifyStatus(false);
    // Middleware auth rejections ('unauthorized'/'forbidden') are fatal for that attempt and
    // won't auto-retry — refresh the token once, then reconnect with it. Guard against loops.
    // ⛔ No session at all — signed out, or the refresh token died and the REST layer ended the
    // session. There is nothing to reconnect WITH, so stop: a socket built by a stray timer after
    // sign-out otherwise retried the handshake for ever against a session that no longer exists.
    if (!tokenStore.access) {
      disconnectSocket();
      return;
    }
    const msg = err?.message ?? '';
    if ((msg === 'unauthorized' || msg === 'forbidden') && !refreshing) {
      refreshing = true;
      void refreshAccessToken()
        .then((ok) => {
          if (ok) socket?.connect(); // auth cb re-reads the fresh token
          // If the refresh token is dead the REST layer's refreshAccess() has already ended the
          // session and started the navigation to /login — don't reconnect into a dead session.
        })
        .catch(() => {})
        .finally(() => {
          refreshing = false;
        });
    }
  });

  return socket;
}

/** Subscribe to reconnect events (fired when the socket comes back after a drop, not first connect). */
export function onSocketReconnect(cb: Handler): () => void {
  reconnectHandlers.add(cb);
  return () => reconnectHandlers.delete(cb);
}

/** Subscribe to connection status changes (true = connected). Fires immediately with current state. */
export function onSocketStatus(cb: StatusHandler): () => void {
  statusHandlers.add(cb);
  cb(!!socket?.connected);
  return () => statusHandlers.delete(cb);
}

export function socketConnected(): boolean {
  return !!socket?.connected;
}

/** Tear down the connection (call on logout so the next user gets a fresh socket). */
export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
  hasConnectedOnce = false;
}
